import { Link } from "react-router-dom";
import { InstallAppButton } from "@/components/InstallAppButton";
import { ArrowRight, Check, Flame, Film, Radar, Send, Share2, Sparkles, Tv, Users, Gift, Shield } from "lucide-react";
import { BuzzlyLogo } from "@/components/BuzzlyLogo";
import { PLAN_FEATURES } from "@/components/Paywall";
import { useAuth } from "@/lib/auth";

const STEPS = [
  [Users, "Choisissez qui surveiller", "Blogueurs, pages, mots-clés : n'importe quelle niche, n'importe quelle langue."],
  [Radar, "Votre agent veille chaque matin", "Il analyse les vraies publications Facebook & TikTok, les commentaires et les partages."],
  [Film, "Vos vidéos sont prêtes", "Montages verticaux avec vrais extraits, voix off et résumé du jour à publier."],
];
const FEATURES = [
  [Flame, "Commentaires en feu", "Repère les débats et les partages massifs avant tout le monde."],
  [Film, "Montage automatique", "Vrais extraits vidéo, titres animés, chiffres et voix off."],
  [Tv, "Résumé du jour", "Une émission de 2-3 min avec les 5 meilleurs buzz."],
  [Send, "Publication en 1 clic", "Facebook aujourd'hui, Instagram et TikTok très bientôt."],
];

function PhoneMock() {
  return (
    <div className="relative mx-auto w-[260px] h-[520px] rounded-[2.4rem] bg-[#0a1020] p-3 shadow-[0_30px_80px_-20px_rgba(24,119,242,0.55)] rotate-[3deg] fade-up" data-testid="hero-phone">
      <div className="h-full w-full rounded-[1.9rem] overflow-hidden bg-gradient-to-b from-[#1b2a4a] to-[#0a1020] relative">
        <div className="absolute top-4 left-4 right-4 flex justify-between items-center">
          <span className="text-[10px] font-bold bg-[#e41e3f] text-white px-2 py-1 rounded">LE RÉSUMÉ DU JOUR</span>
          <span className="text-[11px] font-bold text-[#fac83c]">BUZZ 1/5</span>
        </div>
        <div className="absolute top-14 left-4 right-4 h-48 rounded-xl bg-[radial-gradient(circle_at_30%_30%,#4f8df7,#1b2a4a)] grid place-items-center">
          <Flame className="w-14 h-14 text-white/80" />
        </div>
        <div className="absolute bottom-6 left-4 right-4 space-y-2">
          <span className="inline-block text-[10px] font-bold bg-[#1877f2] text-white px-2 py-1 rounded">BLOGUEUR STAR</span>
          <p className="font-display text-white font-extrabold text-lg leading-tight">LE CLASH QUI ENFLAMME LA TOILE CE MATIN</p>
          <p className="text-[10px] font-bold text-[#fac83c]">12K J'AIME · 9K COMMENTAIRES · 1,7K PARTAGES</p>
          <div className="rounded-lg bg-[#1877f2]/90 p-2 text-[10px] text-white">« Je n'en reviens pas… » — un internaute</div>
          <p className="text-right text-[9px] text-white/70">Créé avec BuzzlyMe · buzzlyme.com</p>
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const cta = user ? "/app" : "/register";
  return (
    <div className="min-h-screen bg-white text-[#050505]" data-testid="landing-page">
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-[#e4e6eb]">
        <div className="max-w-6xl mx-auto px-4 sm:px-5 h-16 flex items-center gap-2 sm:gap-4">
          <BuzzlyLogo />
          <nav className="ml-auto flex items-center gap-0.5 sm:gap-2">
            <a href="#tarif" className="hidden sm:inline px-3 py-2 text-sm font-semibold text-[#65676b] hover:text-[#1877f2]" data-testid="nav-pricing">Tarif</a>
            <Link to={user?.role === "admin" ? "/app/admin" : "/admin"} className="inline-flex items-center gap-1 px-2 sm:px-3 py-2 text-sm font-semibold text-[#65676b] hover:text-[#1877f2]" data-testid="nav-admin"><Shield className="w-4 h-4" /> <span className="hidden sm:inline">Admin</span></Link>
            {!user && <Link to="/login" className="px-2 sm:px-3 py-2 text-sm font-semibold text-[#65676b] hover:text-[#1877f2]" data-testid="nav-login">Connexion</Link>}
            <Link to={cta} className="btn-primary whitespace-nowrap !px-3 sm:!px-5" data-testid="nav-cta">{user ? "Mon dashboard" : "Essai gratuit"}</Link>
          </nav>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-5 pt-14 pb-20 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
        <div className="fade-up">
          <span className="chip chip-fb"><Sparkles className="w-3 h-3" /> Votre agent IA de veille virale</span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05] mt-5 tracking-tight">
            Repérez les buzz <span className="text-[#1877f2]">avant tout le monde.</span> Publiez la vidéo dans la foulée.
          </h1>
          <p className="text-base md:text-lg text-[#65676b] mt-6 max-w-xl">
            BuzzlyMe surveille chaque jour les blogueurs et les mots-clés de votre niche, détecte les publications qui explosent
            et crée pour vous des vidéos courtes prêtes à publier sur Facebook, Instagram et TikTok.
          </p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to={cta} className="btn-primary !px-6 !py-3.5 !text-base" data-testid="hero-cta">Créer mon agent gratuitement <ArrowRight className="w-4 h-4" /></Link>
            <a href="#comment" className="btn-sharp !px-6 !py-3.5" data-testid="hero-how">Comment ça marche</a>
            <InstallAppButton className="btn-sharp !px-6 !py-3.5" testId="hero-install-app-btn" />
          </div>
          <p className="text-xs text-[#8a8d91] mt-4">7 jours gratuits · puis 19,99 €/mois · sans engagement</p>
        </div>
        <PhoneMock />
      </section>

      <section id="comment" className="bg-[#f0f2f5] py-20">
        <div className="max-w-6xl mx-auto px-5">
          <h2 className="font-display text-3xl font-extrabold">Comment ça marche</h2>
          <div className="grid md:grid-cols-3 gap-5 mt-10">
            {STEPS.map(([Icon, t, d], i) => (
              <div key={t} className="bg-white rounded-2xl p-6 shadow-sm fade-up" style={{ animationDelay: `${i * 90}ms` }} data-testid={`step-${i}`}>
                <span className="font-mono-ui text-xs text-[#1877f2]">0{i + 1}</span>
                <Icon className="w-7 h-7 text-[#1877f2] mt-3" />
                <h3 className="font-display text-lg font-bold mt-3">{t}</h3>
                <p className="text-sm text-[#65676b] mt-1.5">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-5 py-20 grid md:grid-cols-2 gap-5">
        {FEATURES.map(([Icon, t, d]) => (
          <div key={t} className="rounded-2xl border border-[#e4e6eb] p-6 flex gap-4 hover:border-[#1877f2] transition-colors">
            <span className="w-11 h-11 rounded-xl bg-[#e7f3ff] grid place-items-center shrink-0"><Icon className="w-5 h-5 text-[#1877f2]" /></span>
            <div><h3 className="font-display font-bold">{t}</h3><p className="text-sm text-[#65676b] mt-1">{d}</p></div>
          </div>
        ))}
      </section>

      <section id="tarif" className="bg-[#1877f2] py-20 text-white">
        <div className="max-w-6xl mx-auto px-5 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold">Un seul tarif. Tout compris.</h2>
            <p className="mt-6 inline-flex items-center gap-2 text-sm bg-white/15 rounded-full px-4 py-2"><Gift className="w-4 h-4" /> Parrainez un ami : 5 € de crédit par abonné parrainé</p>
          </div>
          <div className="bg-white text-[#050505] rounded-2xl p-8 shadow-xl" data-testid="pricing-card">
            <p className="font-semibold text-[#1877f2]">BuzzlyMe Pro</p>
            <p className="font-display text-5xl font-extrabold mt-2">19,99 €<span className="text-base font-semibold text-[#65676b]">/mois</span></p>
            <ul className="mt-6 space-y-2.5">{PLAN_FEATURES.map(f => <li key={f} className="flex gap-2 text-sm"><Check className="w-4 h-4 text-[#1877f2] mt-0.5 shrink-0" /> {f}</li>)}</ul>
            <Link to={cta} className="btn-primary w-full justify-center mt-8 !py-3" data-testid="pricing-cta">Commencer l'essai gratuit</Link>
          </div>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-5 py-10 flex flex-wrap items-center gap-4 text-sm text-[#65676b]">
        <BuzzlyLogo />
        <Link to="/admin" className="inline-flex items-center gap-1.5 font-semibold hover:text-[#1877f2]" data-testid="footer-admin-link"><Shield className="w-4 h-4" /> Espace administrateur</Link>
        <span className="ml-auto inline-flex items-center gap-1.5"><Share2 className="w-4 h-4" /> Chaque vidéo porte la signature « Créé avec BuzzlyMe · buzzlyme.com »</span>
      </footer>
    </div>
  );
}
